import { Form, Formik } from "formik";
import { Dialog, DialogFooter, Icon } from "@fluentui/react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import * as yup from "yup";
import { Flex, HSpace } from "../../../components/styled";
import { RefProvider } from "../../../contexts/RefProvider";
import {
  setCostEstimateRoutine,
  setSetCostEstimateDialog,
} from "../../../store/clients-page/clientsPage";
import { useSelector } from "../../../store/hooks";
import { setYupLocale } from "../../../utils/setYupLocale";
import FormContent from "./FormContent";
import SaveButton from "./SaveButton";
import { Shimmer, ShimmerElementType } from "@fluentui/react";
import { transformNumberValue } from "../../../form/util";

export const SetCostEstimateDialog = () => {
  const [didTrySubmit, setDidTrySubmit] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const schema = useMemo(() => {
    setYupLocale(t);
    return yup.object({
      highCostEstimateMin: yup.string(),
      mediumCostEstimateMin: yup.string(),
    });
  }, [t]);

  const isLoading = useSelector(
    (s) => s.clientsPage.setCostEstimateLoadStatus === "loading",
  );
  const { clientId, body } = useSelector(
    (s) => s.clientsPage.setCostEstimateDialog,
  );

  const onSubmit = (data) => {
    const payload = {
      highCostEstimateMin: transformNumberValue(data?.highCostEstimateMin),
      mediumCostEstimateMin: transformNumberValue(data?.mediumCostEstimateMin),
      poorRangeMin: body?.poorRangeMin,
      moderateRangeMin: body?.moderateRangeMin,
      goodRangeMin: body?.goodRangeMin,
    };

    dispatch(
      setCostEstimateRoutine.trigger({
        clientId: clientId,
        updateHierarchically: data?.updateHierarchically,
        body: {
          clientId: clientId,
          ...payload,
        },
      }),
    );
  };

  const close = () => {
    dispatch(
      setSetCostEstimateDialog({
        clientId: null,
        body: {
          highCostEstimateMin: null,
          mediumCostEstimateMin: null,
          poorRangeMin: null,
          moderateRangeMin: null,
          goodRangeMin: null,
        },
        isOpen: false,
      }),
    );
  };

  const title = (
    <Flex>
      <Icon iconName="Refresh" /> <HSpace />
      <div>{t("ram.clientsList.contextMenu.setCostEstimate")}</div>
    </Flex>
  );

  const initialValues = useMemo(() => {
    return {
      highCostEstimateMin: body?.highCostEstimateMin ?? "",
      mediumCostEstimateMin: body?.mediumCostEstimateMin ?? "",
      poorRangeMin: body?.poorRangeMin ?? "",
      moderateRangeMin: body?.moderateRangeMin ?? "",
      goodRangeMin: body?.goodRangeMin ?? "",
    };
  }, [body]);

  return (
    <Dialog
      hidden={false}
      dialogContentProps={{
        title,
        showCloseButton: true,
      }}
      minWidth={300}
      onDismiss={close}
      isBlocking
    >
      <RefProvider>
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          enableReinitialize
          validateOnChange={didTrySubmit}
          validateOnBlur={didTrySubmit}
          validationSchema={schema}
        >
          {({ submitForm }) => {
            return isLoading ? (
              <Shimmer
                shimmerElements={[
                  {
                    type: ShimmerElementType.line,
                    verticalAlign: "bottom",
                    width: "100%",
                    height: 200,
                  },
                ]}
              />
            ) : (
              <Form>
                <FormContent />
                <DialogFooter>
                  <SaveButton
                    onClick={() => {
                      setDidTrySubmit(true);
                      submitForm();
                    }}
                  />
                </DialogFooter>
              </Form>
            );
          }}
        </Formik>
      </RefProvider>
    </Dialog>
  );
};
